import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { aiReturnReasonsFromModel } from "@redotech/redo-model/return-flow-step-from-model";
import {
  AiReturnReasonsState,
  isAiReturnReasonsStepValid,
} from "@redotech/redo-model/return-flow-step-valid";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import QuestionIcon from "@redotech/redo-web/icon-old/question.svg";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const AI_RETURN_REASONS_STEP_TYPE_NAME = "AI Return Reasons";

export const AI_RETURN_REASONS = (
  Details: React.ComponentType<StepTypeDetailsProps<AiReturnReasonsState>>,
): StepType<AiReturnReasonsState, ModelStep.AiReturnReasons> => ({
  Details: (props: StepTypeDetailsProps<AiReturnReasonsState>) =>
    React.createElement(Details, props),
  title: AI_RETURN_REASONS_STEP_TYPE_NAME,
  valid(state) {
    return isAiReturnReasonsStepValid(state).valid;
  },
  Icon: QuestionIcon,
  description() {
    return "AI-powered return reasons";
  },
  customTitle() {
    return undefined;
  },
  downstream(state) {
    // Order matches the merchant right-panel: damaged, wrong product, custom
    // guidance entries (in array order), then default ("All other cases go...").
    // The position in this array IS the handle index used by updateConnection
    // and the canvas's drag-to-connect interaction, so the two must stay aligned.
    return [
      { id: state.nextDamaged, label: "Damaged" },
      { id: state.nextWrongProduct, label: "Wrong Product" },
      ...(state.customGuidance ?? []).map((g) => ({
        id: g.next,
        label: g.description || "Guidance",
      })),
      { id: state.next, label: "Default" },
    ];
  },
  maxDownstream(state) {
    return 3 + (state.customGuidance?.length ?? 0);
  },
  empty: {
    next: undefined,
    nextWrongProduct: undefined,
    nextDamaged: undefined,
    nextExchange: undefined,
    customGuidance: [],
    additionalAiInfo: undefined,
    maxQuestions: undefined,
    merchantGuidance: undefined,
    customQuestion: undefined,
    customExplanationSubtext: undefined,
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
      if (state.nextWrongProduct === stepId) {
        state.nextWrongProduct = undefined;
      }
      if (state.nextDamaged === stepId) {
        state.nextDamaged = undefined;
      }
      if (state.customGuidance) {
        for (const g of state.customGuidance) {
          if (g.next === stepId) {
            g.next = undefined;
          }
        }
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    // Handle indices match downstream() order: 0=damaged, 1=wrongProduct,
    // 2..N+1=customGuidance[0..N-1], N+2=default.
    return produce(state, (draft) => {
      const customCount = draft.customGuidance?.length ?? 0;
      if (handleIndex === 0) {
        draft.nextDamaged = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextWrongProduct = targetStepId;
      } else if (handleIndex < 2 + customCount) {
        const customIndex = handleIndex - 2;
        const guidance = draft.customGuidance?.[customIndex];
        if (!guidance) {
          throw new Error(
            `Invalid handle index ${handleIndex} for AI Return Reasons step`,
          );
        }
        guidance.next = targetStepId;
      } else if (handleIndex === 2 + customCount) {
        draft.next = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for AI Return Reasons step`,
        );
      }
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
      if (draft.nextWrongProduct === targetStepId) {
        draft.nextWrongProduct = undefined;
      }
      if (draft.nextDamaged === targetStepId) {
        draft.nextDamaged = undefined;
      }
      if (draft.customGuidance) {
        for (const g of draft.customGuidance) {
          if (g.next === targetStepId) {
            g.next = undefined;
          }
        }
      }
    });
  },
  toModel(state, id) {
    return {
      type: ModelStep.AI_RETURN_REASONS,
      next: id(state.next!),
      nextWrongProduct: id(state.nextWrongProduct!),
      nextDamaged: id(state.nextDamaged!),
      // nextExchange is deprecated — never written by new saves.
      customGuidance: state.customGuidance?.length
        ? state.customGuidance.map((g) => ({
            id: g.id,
            description: g.description,
            next: id(g.next!),
          }))
        : undefined,
      additionalAiInfo: state.additionalAiInfo,
      maxQuestions: state.maxQuestions,
      merchantGuidance: state.merchantGuidance,
      customQuestion: state.customQuestion,
      customExplanationSubtext: state.customExplanationSubtext,
    };
  },
  fromModel(model, id) {
    return aiReturnReasonsFromModel(model, id);
  },
});

export {
  type AiReturnReasonsState,
  type CustomGuidanceState,
} from "@redotech/redo-model/return-flow-step-valid";
